import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

interface DefaultMetaTagsProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
}

/**
 * Component to provide default meta tags for all pages
 * Implements proper Open Graph tags for better social media sharing
 */
const DefaultMetaTags: React.FC<DefaultMetaTagsProps> = ({
  title,
  description,
  image = '/favicon.jpg',
  url = 'https://www.aiaj.xyz'
}) => {
  const { language } = useLanguage();
  const currentLang = language === 'ar' ? 'ar' : 'en';

  // Set default title and description based on language
  const defaultTitle = language === 'ar'
    ? 'أكاديمية الذكاء الاصطناعي '
    : 'AI Academy MENA';

  const defaultDescription = language === 'ar'
    ? 'تشكيل مبتكري الغد وتمكين محترفي اليوم من خلال تعليم الذكاء الاصطناعي والتدريب وتطوير المهارات.'
    : 'Shaping tomorrow\'s innovators and empowering today\'s professionals through AI education, training, and skill development.';

  const pageTitle = title ? `${title} | ${defaultTitle}` : defaultTitle;
  const pageDescription = description || defaultDescription;

  // Ensure image URL is absolute
  const imageUrl = image.startsWith('http') ? image : `${url}${image}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLang} />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />

      {/* Canonical URL */}
      <link rel="canonical" href={url} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:alt" content={pageTitle} />
      <meta property="og:locale" content={currentLang === 'ar' ? 'ar_AR' : 'en_US'} />
      <meta property="og:site_name" content={defaultTitle} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={pageTitle} />

      {/* Mobile App Tags */}
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="black-translucent" />
      <meta name="apple-mobile-web-app-title" content="AI Academy" />
      <meta name="application-name" content="AI Academy" />
      <meta name="theme-color" content="#001122" />
    </Helmet>
  );
};

export default DefaultMetaTags;